import { Badge, Button, Card, EmptyState, ErrorState, Skeleton, Avatar } from "@helmr/ui";
import { useMemo, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { NoAccess } from "../../app/NoAccess";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { today as todayOf } from "../../lib/dates";
import type { ChipSpec } from "./status";
import { t } from "./strings";
import s from "./members.module.css";

/** Everything a members or attendance view needs: the client, the grants, today in the business zone, and a cache scope. */
export function useArea() {
  const { client, now } = useServices();
  const ctx = useAppContext();
  const session = useSessionSnapshot();
  const today = todayOf(now(), ctx.timeZone);
  const scope = useMemo(() => [session.businessId, session.branchId, session.branchFilter] as const, [session.businessId, session.branchId, session.branchFilter]);
  const branchId = session.branchFilter === "all" ? undefined : session.branchFilter;
  return { client, ctx, today, scope, branchId, session };
}

export const Chips = ({ chips }: { chips: readonly ChipSpec[] }) => (
  <>{chips.map((c) => <Badge key={c.key} tone={c.tone} dot>{c.label}</Badge>)}</>
);

/** 403 → the shared no-access state; anything else → what failed, Retry and the request id. */
export function Problem({ error, title, onRetry, compact = true }: { error: unknown; title: string; onRetry: () => void; compact?: boolean }) {
  if (isApiError(error) && error.status === 403) return <NoAccess compact={compact} />;
  return <ErrorState compact={compact} title={title} message={isApiError(error) ? error.message : t.problem.generic} requestId={isApiError(error) ? error.requestId : null} onRetry={onRetry} />;
}

export function RowsSkeleton({ rows = 6, label }: { rows?: number; label: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div className={s.skRow} key={i}>
          <Skeleton width={28} height={28} style={{ borderRadius: 14 }} />
          <Skeleton width="28%" />
          <Skeleton width="18%" />
          <Skeleton width="14%" />
        </div>
      ))}
    </div>
  );
}

export function PersonCell({ id, name, to, sub }: { id: string; name: string; to: string; sub?: ReactNode }) {
  return (
    <div className={s.person}>
      <Avatar id={id} name={name} size="sm" />
      <div style={{ minWidth: 0 }}>
        <Link to={to} title={name} onClick={(e) => e.stopPropagation()}>{name}</Link>
        {sub ? <span className={s.sub}>{sub}</span> : null}
      </div>
    </div>
  );
}

export function Field({ id, label, error, hint, children }: { id: string; label: string; error?: string | undefined; hint?: string; children: ReactNode }) {
  return (
    <div className={s.field}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <span className={s.hint}>{hint}</span> : null}
      {error ? <span className={s.err} id={`${id}-err`} role="alert">{error}</span> : null}
    </div>
  );
}

export const FormError = ({ message }: { message: string | null }) => (message ? <div className={s.formErr} role="alert">{message}</div> : null);

export function PageCard({ children, label }: { children: ReactNode; label?: string }) {
  return <Card aria-label={label}>{children}</Card>;
}

export { Button, Card, EmptyState };

/**
 * One Idempotency-Key per user intent (gym-app.md): the same body retried reuses the key, a changed body gets a new one
 * (the same key with another body is a 409 `idempotency_mismatch`), and `reset()` after success starts the next intent.
 */
export function useIntentKey() {
  const ref = useRef<{ body: string; key: string } | null>(null);
  return {
    keyFor(body: unknown): string {
      const text = JSON.stringify(body);
      if (!ref.current || ref.current.body !== text) ref.current = { body: text, key: newIdempotencyKey() };
      return ref.current.key;
    },
    reset() { ref.current = null; },
  };
}

export const errorMessage = (e: unknown) => (isApiError(e) ? e.message : t.problem.generic);

/** `5 Jul 2027`: a calendar date with its year, for plan spans that cross a year. Pinned to UTC so no zone moves the day (rule 3). */
export function dayMonthYear(ymd: string, locale: string): string {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, d)));
}
